var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { createElement as _createElement } from "react";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import classNames from "classnames";
import { Link } from "react-router-dom";
import { useHistory, useLocation } from "react-router";
import useParams from "./useParams";
import ParamsService from "../../Services/Shared/Params.service";
import useUniqueId from "./useUniqueId";
const getClassName = (props) => {
    let className = "sse-tabs ";
    switch (props.tabStyle) {
        case "tabs":
            className += "nav nav-tabs";
            break;
        case "card-header-tabs":
            className += "nav nav-tabs card-header-tabs";
            break;
        case "pills":
            className += "nav nav-pills";
            break;
        case "underline":
            className += "nav nav-pills nav-link-underline";
            break;
        default:
            className += "nav";
    }
    switch (props.letterCasingStyle) {
        case "uppercase":
            className += " text-uppercase";
            break;
        case "lowercase":
            className += " text-lowercase";
            break;
        case "capitalize":
            className += " text-capitalize";
            break;
        case null:
            className += " text-capitalize";
            break;
        default:
            className += " text-capitalize";
            break;
    }
    if (props.fillOrJustified) {
        className += " nav-" + props.fillOrJustified;
    }
    if (props.className) {
        className += " " + props.className;
    }
    return className;
};
const getDropdownClassName = (props) => {
    let className = "";
    switch (props.letterCasingStyle) {
        case "uppercase":
            className += " text-uppercase";
            break;
        case "lowercase":
            className += " text-lowercase";
            break;
        case "capitalize":
            className += " text-capitalize";
            break;
        case null:
            className += " text-capitalize";
            break;
        default:
            className += " text-capitalize";
            break;
    }
    if (props.className) {
        className += " " + props.className;
    }
    return className;
};
const getTabIndex = (tabId, tabs) => {
    if (!(tabs === null || tabs === void 0 ? void 0 : tabs.length) || tabId == null) {
        return null;
    }
    // See if any tabs have an id that match this one
    let index = tabs.findIndex((tab) => (tab === null || tab === void 0 ? void 0 : tab.id) === tabId);
    if (index === -1) {
        // see if the tabId is a number and if so, set that as the index, otherwise set it to 0.
        // @ts-expect-error - TS2345 - Argument of type 'string | number' is not assignable to parameter of type 'string'.
        index = parseInt(tabId, 10) || 0;
        // if the current tab is out of range, default it to 0
        if (index >= tabs.length) {
            index = 0;
        }
    }
    return index;
};
/* eslint-disable jsdoc/check-param-names */
/**
 * Creates navigable tabs.
 *
 * @param {Tab[]} tabs - the list of tabs
 * @param {string} tabStyle - the styling of the tabs
 * @param {string?} enableRouting - set to a string to store which tab in the URL so tabs can be deep-linked
 * @param {number} defaultTab - the default tab.  will not work with routing.
 * @returns {[]} [tabs, content, updateTabs function]
 */
const useTabs = (_a) => {
    var _b, _c;
    var { tabs, fillOrJustified, tabStyle, letterCasingStyle, className, enableRouting, defaultTab, keepAllTabsMounted, otherURLParamsToChangeOnTabChange, forceAsDropdown, collapseToDropdown } = _a, otherProps = __rest(_a, ["tabs", "fillOrJustified", "tabStyle", "letterCasingStyle", "className", "enableRouting", "defaultTab", "keepAllTabsMounted", "otherURLParamsToChangeOnTabChange", "forceAsDropdown", "collapseToDropdown"]);
    const [localCurrentTab, setLocalCurrentTab] = React.useState(defaultTab || 0);
    const params = useParams();
    const routerHistory = useHistory();
    const routerLocation = useLocation();
    const makeId = useUniqueId()[1];
    // @ts-expect-error - TS2533 - Object is possibly 'null' or 'undefined'. | TS2339 - Property 'filter' does not exist on type 'Tab | false[]'. | TS7006 - Parameter 'tab' implicitly has an 'any' type.
    const safeTabs = tabs.filter((tab) => tab);
    const tabId = enableRouting ? params[enableRouting] || 0 : localCurrentTab;
    const safeTabsIndex = getTabIndex(tabId, safeTabs);
    let currentTabNode = null;
    if (safeTabsIndex != null) {
        if (!keepAllTabsMounted) {
            const comp = safeTabs[safeTabsIndex].content;
            currentTabNode = typeof comp === "function" ? comp(true) : comp;
        }
        else {
            currentTabNode = safeTabs.map((tab, index) => (_jsx("div", { className: classNames({ "d-none": index !== safeTabsIndex }), role: "tabpanel", "aria-labelledby": makeId(index), children: typeof tab.content === "function"
                    ? tab.content(index === safeTabsIndex)
                    : tab.content }, index)));
        }
    }
    const updateTab = React.useCallback((tab) => {
        if (enableRouting) {
            // If routing is enabled, we need to navigate.
            routerHistory.replace(Object.assign(Object.assign({}, routerLocation), { search: ParamsService.calcNewParams(Object.assign(Object.assign({}, (otherURLParamsToChangeOnTabChange || {})), { [enableRouting]: tab })) }));
        }
        else {
            // If routing is not enabled, if the tab is a string, we need to find the id, if not, then assume it's an index.
            let index;
            if (typeof tab === "string") {
                index = safeTabs.findIndex(({ id }) => id === tab);
            }
            else {
                index = tab;
            }
            if (index >= 0 && index < safeTabs.length) {
                setLocalCurrentTab(index);
            }
        }
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
        enableRouting,
        otherURLParamsToChangeOnTabChange,
        routerHistory,
        routerLocation,
        safeTabs,
    ]);
    const tabClassName = getClassName({
        tabStyle,
        letterCasingStyle,
        fillOrJustified,
        className,
    });
    const displayAsTabs = (_createElement("ul", Object.assign({}, otherProps, { className: tabClassName, role: "tablist", key: 0 }), safeTabs.map((tab, index) => {
        const isSelected = safeTabsIndex === index;
        return (_jsx("li", { className: "nav-item", role: "presentation", children: enableRouting ? (_jsx(Link, { to: (location) => (Object.assign(Object.assign({}, location), { search: ParamsService.calcNewParams(Object.assign(Object.assign({}, (otherURLParamsToChangeOnTabChange || {})), { [enableRouting]: tab.id || index })) })), id: makeId(index), className: "nav-link" + (isSelected ? " active" : ""), "aria-selected": isSelected, role: "tab", replace: true, children: typeof tab.tab === "function" ? tab.tab(isSelected) : tab.tab })) : (_jsx("button", { className: "nav-link " +
                    (safeTabsIndex === index ? " active" : " clickable"), id: makeId(index), onClick: () => updateTab(index), role: "tab", type: "button", "aria-selected": isSelected, children: typeof tab.tab === "function" ? tab.tab(isSelected) : tab.tab })) }, index));
    })));
    const dropdownClassName = getDropdownClassName({
        letterCasingStyle,
        className,
    });
    const selectedTab = safeTabsIndex != null ? (_c = (_b = safeTabs[safeTabsIndex]) === null || _b === void 0 ? void 0 : _b.tab) !== null && _c !== void 0 ? _c : null : null;
    const displayTabsAsDropdown = (_jsxs("div", { className: "dropdown border-bottom", style: { paddingBottom: "8px" }, children: [_jsx("button", { className: "btn btn-primary dropdown-toggle w-100 text-left font-weight-bold" +
                    dropdownClassName, type: "button", id: "dropdownMenuButton", "data-toggle": "dropdown", "aria-haspopup": "true", "aria-expanded": "false", children: typeof selectedTab === "function" ? selectedTab(true) : selectedTab }), _jsx("div", { className: "dropdown-menu shadow custom-scrollbar w-100", "aria-labelledby": "dropdownMenuButton", style: { maxHeight: 150, height: "auto", overflowX: "hidden" }, children: safeTabs.map((tab, index) => {
                    const isSelected = safeTabsIndex === index;
                    return (_jsx("button", { className: "dropdown-item" + dropdownClassName, style: isSelected
                            ? {
                                fontWeight: "bold",
                            }
                            : { fontWeight: "normal" }, onClick: () => updateTab(index), children: typeof tab.tab === "function" ? tab.tab(false) : tab.tab }, index));
                }) })] }, 0));
    const currentTabContent = safeTabsIndex != null && (_jsx("div", { role: "tabpanel", "aria-labelledby": makeId(safeTabsIndex), children: currentTabNode }, 1));
    if (forceAsDropdown) {
        return [
            displayTabsAsDropdown,
            currentTabContent,
            // @ts-expect-error - TS2345 - Argument of type 'Tab | false[] | null | undefined' is not assignable to parameter of type 'readonly (Tab | null | undefined)[]'.
            getTabIndex(tabId, tabs),
            updateTab,
        ];
    }
    if (collapseToDropdown) {
        const { componentWidth, tabsAmountThreshold, widthThreshold } = collapseToDropdown;
        const validTabAmount = tabsAmountThreshold
            ? safeTabs.length > tabsAmountThreshold
            : true;
        const navigationStyle = (componentWidth > widthThreshold && !forceAsDropdown) ||
            (componentWidth < widthThreshold && !validTabAmount)
            ? displayAsTabs
            : displayTabsAsDropdown;
        return [
            navigationStyle,
            currentTabContent,
            // @ts-expect-error - TS2345 - Argument of type 'Tab | false[] | null | undefined' is not assignable to parameter of type 'readonly (Tab | null | undefined)[]'.
            getTabIndex(tabId, tabs),
            updateTab,
        ];
    }
    return [
        displayAsTabs,
        currentTabContent,
        // @ts-expect-error - TS2345 - Argument of type 'Tab | false[] | null | undefined' is not assignable to parameter of type 'readonly (Tab | null | undefined)[]'.
        getTabIndex(tabId, tabs),
        updateTab,
    ];
};
export default useTabs;
